import { DateTime } from '@ksyos/date-time';
import { Box, DatePicker, Text } from '@ksyos/design-system';
import {} from 'immutable';
import { useEffect, useState } from 'react';
import type { ServiceType } from '@common/enums';
import type { IContractSettingAddEditValidated } from '@common/validation/contractSetting';
import type { ValidationErrors } from '@frontend/utils';
import { validateDatetimeOverlap } from '@common/validation/utils/validateDatetimeOverlap';
import { emptyValidationErrors } from '@frontend/utils';
import { useFormContext } from '@frontend/utils/hooks/useFormContext';
import type { OrganizationContractSettingsFormState } from './OrganizationContractSettings';

interface OrganizationContractSettingsDateProps {
    index: number;
    isEditing: boolean;
    serviceType: ServiceType;
    variant: 'startDate' | 'endDate';
}

export function OrganizationContractSettingsDate(props: Readonly<OrganizationContractSettingsDateProps>) {
    const form = useFormContext<OrganizationContractSettingsFormState>();

    const [contractSettingDate, setContractSettingDate] = useState<Date | null>(null);
    const [validationError, setValidationError] = useState<string>();

    useEffect(() => {
        const previousContractSettingDate = form.values[props.serviceType][props.index][props.variant];
        if (previousContractSettingDate) {
            setContractSettingDate(DateTime.from(previousContractSettingDate).toDate());
        } else {
            setContractSettingDate(null);
        }
    }, [form.values]);

    useEffect(() => {
        if (form.errors.has(`${props.serviceType}.${props.index}.${props.variant}`)) {
            setValidationError(form.errors.getMessage(`${props.serviceType}.${props.index}.${props.variant}`));
        } else {
            setValidationError(undefined);
        }
    }, [form.errors]);

    if (props.isEditing) {
        return (
            <DatePicker
                error={validationError}
                name={`${props.serviceType}.${props.index}.${props.variant}`}
                onBlur={() => {
                    const dateUpdate = getStartOrEndOfMonthFromDate(props.variant, contractSettingDate);
                    handleDateChange(dateUpdate);
                    setContractSettingDate(dateUpdate ? DateTime.from(dateUpdate).toDate() : null);
                }}
                onChange={(newDate) => {
                    handleDateChange(newDate ? DateTime.from(newDate).format('YYYY-MM-DD') : null);
                    setContractSettingDate(newDate);
                }}
                size="large"
                value={contractSettingDate}
            />
        );
    }

    return (
        <Box minHeight="40px">
            <Text pt={2}>{contractSettingDate ? DateTime.from(contractSettingDate).format('DD-MM-YYYY') : '-'}</Text>
        </Box>
    );

    function handleDateChange(newDate: string | null) {
        form.handleChange({
            manualValidationFn: (triggeredBy, formValues) =>
                validateOverlappingDates(triggeredBy, formValues[props.serviceType]),
            name: `${props.serviceType}.${props.index}.${props.variant}`,
            value: newDate,
        });
    }

    function getStartOrEndOfMonthFromDate(
        variant: OrganizationContractSettingsDateProps['variant'],
        date: Date | null
    ) {
        return date
            ? DateTime.from(date)[variant === 'startDate' ? 'startOf' : 'endOf']('month').format('YYYY-MM-DD')
            : null;
    }

    function validateOverlappingDates(triggeredBy: string, contractSettings: IContractSettingAddEditValidated[]) {
        let combinedErrors: ValidationErrors = emptyValidationErrors;

        const validatedDates = validateDatetimeOverlap(
            contractSettings.map((contractSetting) => ({
                endDate: contractSetting.endDate,
                errors: emptyValidationErrors,
                fieldName: triggeredBy,
                startDate: contractSetting.startDate,
            }))
        );

        const hasErrors = validatedDates.some((dates) => dates.errors.size > 0);

        if (hasErrors) {
            validatedDates
                .filter((dates) => dates.errors.size > 0)
                .flatMap((date) => date.errors)
                .forEach((errorMap) => {
                    combinedErrors = combinedErrors.merge(errorMap);
                });
        }

        return combinedErrors;
    }
}
